import { Telemetry, ValidTelemetryEvent } from '~commonInfra/services/Telemetry';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

/**
 * PFT (Product Feature Telemetry) event fired when a user changes the contact
 * (data steward / point of contact) on an external catalog asset (database or
 * table). Mapped in Juno to:
 *   Tableau Cloud > Catalog > External Asset Details (Cloud) > Modify Contact
 *
 * Wire event name: `contact_modified`
 * Action verb:     `completed` (added downstream by Juno-registered schema)
 * Org ID type:     `tenant_id`
 *
 * Emitted after the `setContact` REST call resolves successfully (i.e., the
 * resolved IResult carries no errors). This matches catalog-ui's flipped
 * post-review behavior so both halves of the shared Juno schema mean
 * "successful save", not "attempted save".
 *
 * PII discipline: we intentionally do NOT log the contact user id, name,
 * email, or any free-text. Only categorical descriptors (content type,
 * connector, action discriminator) and a boolean for prior-contact state.
 */
export const CONTACT_MODIFIED_EVENT_TYPE = 'contact_modified';

export type ContactModifiedAction = 'set_first_time' | 'changed';

export interface ContactModifiedTelemetryEvent extends ValidTelemetryEvent {
  /** Wire event name. Always `contact_modified`. */
  type: typeof CONTACT_MODIFIED_EVENT_TYPE;
  /**
   * PFT (Product Feature Telemetry) Standard Attribute for tenant routing. On Tableau Cloud
   * the tenant is the site, so this equals `site_luid`. Set per-event on the EAD telemetry
   * helpers rather than on `Telemetry.getCommonProperties()` so non-EAD vpc events (owned by
   * other teams) don't inherit a Standard Attribute their schemas don't declare.
   */
  tenant_id: string;
  /** Catalog content type the contact is being applied to. `'database'` or `'table'`. */
  content_type: string;
  /**
   * Connector backend (snowflake / databricks / postgres / ...) when available. Empty string
   * when undefined. Populated for database and table content via `DatabaseAndTableMapper`,
   * and for virtualConnection / virtualConnectionTable via the PublishedConnection mappers
   * once the upstream REST `connectionType` field is populated by the server-side change
   * (W-22931576 / monolith follow-up). The `contact_modified` emit site doesn't actually
   * fire for VC/VCT today -- contact editing is currently a database/table-only feature --
   * but the wire shape is shared across all three EAD event helpers for consistency.
   */
  connection_type: string;
  /** `'set_first_time'` when the asset had no contact previously; `'changed'` when replacing an existing contact. */
  action: ContactModifiedAction;
  /** True when the asset already had a contact before this change. Redundant with `action` but easier to filter on in the dashboard. */
  had_previous_contact: boolean;
}

/**
 * Emit `contact_modified` for an external catalog asset. The `.completed`
 * action verb is registered on the Juno schema and applied by Endeavour
 * downstream -- the wire `type` field is the bare event name.
 *
 * Caller passes the external content type ('database' | 'table') and the
 * content-info object so we can read `catalogConnectionType` (set by
 * `DatabaseAndTableMapper` -- named distinctly from
 * `IDataConnectionInfoProperties.connectionType` to avoid an interface
 * extends-clash; see IContentInfo for the rationale) and detect whether a
 * prior contact existed.
 */
export function sendContactModifiedTelemetry(
  externalContentType: string,
  externalContentItem: IContentInfo,
): void {
  const hadPreviousContact = Boolean(externalContentItem.contact);

  Telemetry.sendEvent<ContactModifiedTelemetryEvent>({
    type: CONTACT_MODIFIED_EVENT_TYPE,
    tenant_id: getSessionManagementServiceInstance().sessionSiteLuid(),
    content_type: externalContentType,
    connection_type: externalContentItem.catalogConnectionType ?? '',
    action: hadPreviousContact ? 'changed' : 'set_first_time',
    had_previous_contact: hadPreviousContact,
  });
}
